import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'react-icons/fa';
import { fetchReadme } from '../services/githubService';

const { FaGithub, FaExternalLinkAlt, FaFileAlt } = Icons;

// Helper to render icons safely
const Icon = ({ Component, ...props }: { Component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

interface ProjectCardProps {
  name: string;
  description: string;
  tech: string[];
  github?: string;
  demo?: string;
  type: 'public' | 'private' | 'freelance';
  preview?: string;
  fullName?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  name,
  description,
  tech,
  github,
  demo,
  type,
  preview,
  fullName
}) => {
  const [hovered, setHovered] = useState(false);
  const [showReadme, setShowReadme] = useState(false);
  const [readmeContent, setReadmeContent] = useState<string | null>(null);
  const [loadingReadme, setLoadingReadme] = useState(false);

  const handleReadmeClick = async () => {
    if (showReadme) {
      setShowReadme(false);
      return;
    }

    if (readmeContent) {
      setShowReadme(true);
      return;
    }

    if (fullName) {
      setLoadingReadme(true);
      const [owner, repo] = fullName.split('/');
      
      // Freelance projeler için önce local README'yi dene
      if (type === 'freelance') {
        try {
          const localResponse = await fetch(`/readmes/${repo.toLowerCase()}.md`);
          if (localResponse.ok) {
            const localContent = await localResponse.text();
            setReadmeContent(localContent);
            setLoadingReadme(false);
            setShowReadme(true);
            return;
          }
        } catch (e) {
          // Local README bulunamazsa GitHub'dan çek
        }
      }
      
      // GitHub'dan README çek
      const content = await fetchReadme(owner, repo, type === 'freelance');
      setReadmeContent(content);
      setLoadingReadme(false);
      setShowReadme(true);
    }
  };

  const isFreelance = type === 'freelance';

  return (
    <div
      className="relative group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`rounded-2xl p-6 backdrop-blur-sm transition-all h-full ${
          isFreelance
            ? 'bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-indigo-500/10 border border-pink-500/20 hover:border-pink-500/40'
            : 'bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-indigo-500/20 hover:border-indigo-500/40'
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className={`text-2xl ${isFreelance ? 'text-pink-400' : 'text-indigo-400'}`}>
            {isFreelance ? '🔗' : '💻'}
          </div>
          <div className="flex gap-2">
            {github && (
              <a
                href={github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-indigo-400 transition-colors"
                title="GitHub Repository"
              >
                <Icon Component={FaGithub} />
              </a>
            )}
            {demo && demo !== '#' && (
              <a
                href={demo}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-indigo-400 transition-colors"
                title="Live Demo"
              >
                <Icon Component={FaExternalLinkAlt} />
              </a>
            )}
            {fullName && (
              <button
                onClick={handleReadmeClick}
                className="text-gray-400 hover:text-indigo-400 transition-colors"
                title="View README"
              >
                <Icon Component={FaFileAlt} />
              </button>
            )}
          </div>
        </div>

        <h3 className="text-xl font-semibold text-white mb-2">{name}</h3>
        <p className="text-gray-300 mb-4 text-sm">{description || 'No description available'}</p>
        
        {/* Tooltip on hover */}
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-2 right-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded shadow-lg z-10"
          >
            💡 Proje bazlı bilgi sağlanabilecektir
          </motion.div>
        )}

        <div className="flex flex-wrap gap-2">
          {tech.map((t) => (
            <span
              key={t}
              className={`px-2 py-1 rounded text-xs border ${
                isFreelance
                  ? 'bg-pink-500/20 border-pink-500/30 text-pink-300'
                  : 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300'
              }`}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Preview on Hover */}
      {hovered && preview && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute top-full left-0 right-0 mt-2 bg-black/95 border border-purple-500/30 rounded-lg p-3 z-50 shadow-2xl"
          style={{ minWidth: '100%', width: '100%' }}
        >
          <div className="bg-gray-800 rounded overflow-hidden relative" style={{ height: '300px' }}>
            <iframe
              src={preview}
              className="w-full h-full pointer-events-none"
              title="Preview"
              sandbox="allow-same-origin allow-scripts"
              style={{ 
                border: 'none', 
                width: '200%', 
                height: '200%', 
                transform: 'scale(0.5)', 
                transformOrigin: 'top left'
              }}
              loading="lazy"
            />
            <div className="absolute bottom-3 left-3 bg-black/90 text-white text-xs px-3 py-1.5 rounded backdrop-blur-sm border border-purple-500/30">
              💡 Proje bazlı bilgi sağlanabilecektir
            </div>
          </div>
        </motion.div>
      )}

      {/* README Modal */}
      {showReadme && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowReadme(false)}
          >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-slate-900 rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto border border-indigo-500/30"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-white">{name} - README</h3>
                  <button
                    onClick={() => setShowReadme(false)}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
                {loadingReadme ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
                    <p className="text-gray-400 mt-4">Loading README...</p>
                  </div>
                ) : readmeContent ? (
                  <div className="prose prose-invert max-w-none">
                    <div className="text-gray-300 bg-slate-800 p-4 rounded-lg overflow-auto max-h-[60vh]">
                      <pre className="whitespace-pre-wrap text-gray-300 font-mono text-sm" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {readmeContent}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">README not found</p>
                )}
              </motion.div>
            </motion.div>
        </>
      )}
    </div>
  );
};

export default ProjectCard;
